import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, Chip } from '@mui/material';
import { Delete, Add, Edit, Visibility, VisibilityOff, Summarize, ExpandMore, ChevronRight, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Account, PostingClass, SegmentFieldPolicy } from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    padding: '32px',
    borderRadius: '8px'
};

const ACCOUNT_TYPES: Account['accountType'][] = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

export const deriveSegmentPolicy = (type: Account['accountType']): SegmentFieldPolicy =>
    (type === 'REVENUE' || type === 'EXPENSE') ? 'REQUIRED' : 'OPTIONAL';

const POSTING_CLASS_OPTIONS: PostingClass[] = [
    'BALANCE_SHEET', 'RECEIVABLE', 'PAYABLE', 'CASH', 'TAX',
    'EXPENSE', 'REVENUE', 'INVENTORY', 'GR_IR_CLEARING', 'INTER_SEGMENT_CLEARING'
];

// Significant prefix of a code = the code with its trailing zeros removed; a child's code
// always starts with its ancestor's significant prefix (the same convention the Level
// column and indentation already rely on).
function sigPrefix(code: string): string {
    return code.replace(/0+$/, '') || code;
}

function isDescendant(child: { code: string }, parent: { code: string }): boolean {
    return child.code !== parent.code
        && getAccountLevel(child.code) > getAccountLevel(parent.code)
        && child.code.startsWith(sigPrefix(parent.code));
}

function getAccountLevel(code: string): number {
    if (!code || code.length !== 10) return 1;
    if (code.endsWith('00000000')) return 1;
    if (code.endsWith('000000')) return 2;
    if (code.endsWith('0000')) return 3;
    if (code.endsWith('00')) return 4;
    return 5;
}

const ChartOfAccounts: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [hiddenAccountIds, setHiddenAccountIds] = useState<Set<string>>(new Set());
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [showHidden, setShowHidden] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
    const [formData, setFormData] = useState<{
        code: string;
        name: string;
        accountType: Account['accountType'];
        description: string;
        parentAccountId: string;
        postingClass: PostingClass | '';
        segmentFieldPolicy: SegmentFieldPolicy;
    }>({
        code: '',
        name: '',
        accountType: 'ASSET',
        description: '',
        parentAccountId: '',
        postingClass: '',
        segmentFieldPolicy: 'OPTIONAL'
    });

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const response = await AccountService.getAccountsByInstitution(0, 500, true);
            const data = response.content ? response.content : response;
            setAccounts(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.showApiError(err, 'Failed to load accounts');
        } finally {
            setLoading(false);
        }
    };

    const fetchHiddenAccounts = async () => {
        try {
            const hidden = await AccountService.getHiddenAccounts();
            setHiddenAccountIds(new Set(hidden));
        } catch (err) {
            console.error('Error fetching hidden accounts:', err);
        }
    };

    useEffect(() => {
        fetchAccounts();
        fetchHiddenAccounts();
    }, []);

    const handleToggleVisibility = async (account: Account) => {
        try {
            if (hiddenAccountIds.has(account.id!)) {
                await AccountService.unhideAccount(account.id!);
                setHiddenAccountIds(prev => {
                    const next = new Set(prev);
                    next.delete(account.id!);
                    return next;
                });
            } else {
                await AccountService.hideAccount(account.id!);
                setHiddenAccountIds(prev => new Set(prev).add(account.id!));
            }
        } catch (err) {
            toast.showApiError(err, 'Failed to update account visibility');
        }
    };

    const handleOpenFormModal = (account?: Account) => {
        if (account) {
            setSelectedAccount(account);
            setFormData({
                code: account.code,
                name: account.name,
                accountType: account.accountType,
                description: account.description || '',
                parentAccountId: account.parentAccountId || '',
                postingClass: account.postingClass ?? '',
                segmentFieldPolicy: account.segmentFieldPolicy ?? 'OPTIONAL'
            });
        } else {
            setSelectedAccount(null);
            setFormData({
                code: '',
                name: '',
                accountType: 'ASSET',
                description: '',
                parentAccountId: '',
                postingClass: '',
                segmentFieldPolicy: 'OPTIONAL'
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedAccount(null);
    };

    const handleOpenDeleteModal = (account: Account) => {
        setSelectedAccount(account);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedAccount(null);
    };

    const handleDelete = async () => {
        if (!selectedAccount) return;
        try {
            await AccountService.deleteAccount(selectedAccount.id!);
            await fetchAccounts();
            handleCloseDeleteModal();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete account');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const institutionId = AuthService.getSelectedInstitution();
            const accountData: Account = {
                code: formData.code,
                name: formData.name,
                accountType: formData.accountType,
                description: formData.description || undefined,
                parentAccountId: formData.parentAccountId || undefined,
                institutionId,
                ...(formData.postingClass ? { postingClass: formData.postingClass } : {}),
                segmentFieldPolicy: formData.segmentFieldPolicy
            };
            if (selectedAccount) {
                await AccountService.updateAccount(selectedAccount.id!, accountData);
            } else {
                await AccountService.createAccount(accountData);
            }
            await fetchAccounts();
            handleCloseFormModal();
        } catch (err) {
            toast.showApiError(err, 'Failed to save account');
        }
    };

    const displayedAccounts = showHidden
        ? accounts
        : accounts.filter(a => !hiddenAccountIds.has(a.id!));

    const groupedAccounts = ACCOUNT_TYPES.reduce((acc, type) => {
        acc[type] = displayedAccounts
            .filter(a => a.accountType === type)
            .sort((a, b) => a.code.localeCompare(b.code));
        return acc;
    }, {} as Record<string, Account[]>);

    const toggleCollapsed = (code: string) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const parentCodes = displayedAccounts
        .filter(a => displayedAccounts.some(o => isDescendant(o, a)))
        .map(a => a.code);

    const handleCollapseExpandAll = () => {
        setCollapsed(prev => (prev.size > 0 ? new Set() : new Set(parentCodes)));
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Chart of Accounts</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleCollapseExpandAll}
                        startIcon={collapsed.size > 0 ? <UnfoldMore /> : <UnfoldLess />}
                    >
                        {collapsed.size > 0 ? 'Expand All' : 'Collapse All'}
                    </Button>
                    <Button
                        variant="outlined"
                        color={showHidden ? 'primary' : 'inherit'}
                        onClick={() => setShowHidden(!showHidden)}
                        startIcon={showHidden ? <Visibility /> : <VisibilityOff />}
                    >
                        {showHidden ? 'Showing All' : 'Show Hidden'}
                    </Button>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpenFormModal()}>
                        Add Account
                    </Button>
                </div>
            </div>

            {ACCOUNT_TYPES.map(type => (
                <div key={type} style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px', color: '#333' }}>{type}</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={tableHeaderStyle}>Code</th>
                                <th style={{ ...tableHeaderStyle, width: '60px' }}>Level</th>
                                <th style={tableHeaderStyle}>Name</th>
                                <th style={tableHeaderStyle}>Description</th>
                                <th style={tableHeaderStyle}>Posting Class</th>
                                <th style={tableHeaderStyle}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedAccounts[type]?.length === 0 ? (
                                <tr>
                                    <td style={tableCellStyle} colSpan={6}>No accounts in this category</td>
                                </tr>
                            ) : (
                                groupedAccounts[type]?.filter(account =>
                                    !groupedAccounts[type].some(p => collapsed.has(p.code) && isDescendant(account, p))
                                ).map(account => {
                                    const isHidden = hiddenAccountIds.has(account.id!);
                                    const isGlobal = !account.institutionId;
                                    const level = getAccountLevel(account.code);
                                    const indentPx = (level - 1) * 16;
                                    const hasChildren = groupedAccounts[type].some(o => isDescendant(o, account));
                                    const isCollapsed = collapsed.has(account.code);
                                    return (
                                        <tr key={account.id} style={isHidden ? { opacity: 0.5 } : undefined}>
                                            <td style={tableCellStyle}>{account.code}</td>
                                            <td style={{ ...tableCellStyle, color: '#666', fontSize: '12px', fontWeight: level === 1 ? 700 : 400 }}>L{level}</td>
                                            <td style={{ ...tableCellStyle, paddingLeft: `${hasChildren ? indentPx : 12 + indentPx + 28}px`, fontWeight: level <= 2 ? 600 : 400 }}>
                                                {hasChildren && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleCollapsed(account.code)}
                                                        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${account.code}`}
                                                        sx={{ mr: 0.5 }}
                                                    >
                                                        {isCollapsed ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                    </IconButton>
                                                )}
                                                {account.name}
                                                {isGlobal && (
                                                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888', backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                                                        System
                                                    </span>
                                                )}
                                            </td>
                                            <td style={tableCellStyle}>{account.description || '-'}</td>
                                            <td style={tableCellStyle}>
                                                {account.postingClass
                                                    ? <Chip label={account.postingClass} size="small" />
                                                    : <span style={{ color: '#bbb' }}>—</span>}
                                            </td>
                                            <td style={tableCellStyle}>
                                                <Tooltip title="View Ledger">
                                                    <IconButton size="small" onClick={() => navigate(`/accounting/accounts/${account.id}/ledger`)} color="primary" aria-label="view ledger">
                                                        <Summarize />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isHidden ? 'Show account' : 'Hide account'}>
                                                    <IconButton size="small" onClick={() => handleToggleVisibility(account)} color={isHidden ? 'default' : 'primary'} aria-label={isHidden ? 'Show account' : 'Hide account'}>
                                                        {isHidden ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </Tooltip>
                                                {!isGlobal && (
                                                    <>
                                                        <IconButton size="small" onClick={() => handleOpenFormModal(account)} color="primary" aria-label="edit">
                                                            <Edit />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => handleOpenDeleteModal(account)} color="error" aria-label="delete">
                                                            <Delete />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            ))}

            {/* Form Modal */}
            <Modal open={isFormModalOpen} onClose={handleCloseFormModal}>
                <div style={modalStyle}>
                    <h2>{selectedAccount ? 'Edit Account' : 'Add Account'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Code"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            margin="normal"
                        />
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Account Type</InputLabel>
                            <Select
                                data-testid="account-type-select"
                                value={formData.accountType}
                                label="Account Type"
                                onChange={(e) => {
                                    const accountType = e.target.value as Account['accountType'];
                                    setFormData({ ...formData, accountType, segmentFieldPolicy: deriveSegmentPolicy(accountType) });
                                }}
                            >
                                {ACCOUNT_TYPES.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Parent Account (Optional)</InputLabel>
                            <Select
                                value={formData.parentAccountId}
                                label="Parent Account (Optional)"
                                onChange={(e) => setFormData({ ...formData, parentAccountId: e.target.value })}
                            >
                                <MenuItem value="">None</MenuItem>
                                {accounts
                                    .filter(a => a.id !== selectedAccount?.id)
                                    .map(a => (
                                        <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Posting Class</InputLabel>
                            <Select
                                value={formData.postingClass}
                                label="Posting Class"
                                onChange={(e) => setFormData({ ...formData, postingClass: e.target.value as PostingClass | '' })}
                            >
                                <MenuItem value="">(none)</MenuItem>
                                {POSTING_CLASS_OPTIONS.map(pc => (
                                    <MenuItem key={pc} value={pc}>{pc}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Segment Policy</InputLabel>
                            <Select
                                data-testid="account-segment-policy"
                                value={formData.segmentFieldPolicy}
                                label="Segment Policy"
                                onChange={(e) => setFormData({ ...formData, segmentFieldPolicy: e.target.value as SegmentFieldPolicy })}
                            >
                                <MenuItem value="OPTIONAL">Optional</MenuItem>
                                <MenuItem value="REQUIRED">Required</MenuItem>
                            </Select>
                        </FormControl>
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button onClick={handleCloseFormModal}>Cancel</Button>
                            <Button type="submit" variant="contained" color="primary">
                                {selectedAccount ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
                <div style={modalStyle}>
                    <h2>Delete Account</h2>
                    <p>Are you sure you want to delete account "{selectedAccount?.name}"?</p>
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button onClick={handleCloseDeleteModal}>Cancel</Button>
                        <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ChartOfAccounts;
